// Helper to extract a usable error message from any error shape (FastAPI / axios)
export function getErrorMessage(err, fallback = 'Something went wrong') {
  // Pull common axios fields up front so we can branch on them
  const status = err?.response?.status;
  const code = err?.code;          // axios codes: 'ECONNABORTED', 'ERR_NETWORK', etc.
  const message = err?.message || '';

  // Network-layer failures arrive BEFORE we have a response payload. These are
  // the "Failed to fetch" / "Network Error" cases. They typically mean:
  //   - request timed out (axios timeout reached or proxy idle-timeout)
  //   - server connection dropped mid-request
  //   - DNS / CORS preflight failed
  // We surface a more actionable message so the user doesn't see the cryptic
  // browser default.
  if (!err?.response) {
    if (code === 'ECONNABORTED' || /timeout/i.test(message)) {
      return 'Request timed out — the server took too long to respond. For long renders (PDF download / POV generation), this can happen if the upstream proxy drops the connection. Try the operation again, or download a smaller deck if you can.';
    }
    if (code === 'ERR_NETWORK' || /Network Error|Failed to fetch/i.test(message)) {
      return 'Network error — the browser could not complete the request. Common causes: a stale cached page (try a hard refresh: Ctrl+Shift+R), a browser extension blocking the call, a VPN/proxy quirk, or temporary connectivity loss. If it keeps happening, please open DevTools → Network and check the failing request.';
    }
    return message || fallback;
  }

  // 5xx without a specific detail body
  if (status === 502 || status === 503 || status === 504) {
    return 'Upstream service unavailable (likely an AI provider hiccup). Please retry in a minute.';
  }

  // When the caller asked for responseType=blob (e.g. PDF download) AND the
  // server returned a JSON error, axios hands us the JSON wrapped in a Blob.
  // We can't synchronously await blob.text() here, but we can at least give
  // a less cryptic message. The caller can choose to read the blob itself if
  // it wants the exact server detail.
  const data = err?.response?.data;
  if (data instanceof Blob) {
    if (status === 401 || status === 403) return 'You are not authorised to download this PDF (please log out and back in).';
    if (status === 402 || status === 429) return 'Quota or rate limit reached on this download.';
    if (status >= 500) return 'The PDF service returned an error. Please retry in a minute; if it persists check the server logs.';
    return `Download failed (HTTP ${status}).`;
  }

  const detail = data?.detail;
  if (!detail) return message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (typeof first === 'string') return first;
    if (first?.msg) return first.msg;
    try { return JSON.stringify(first); } catch { return fallback; }
  }
  if (typeof detail === 'object') {
    if (detail.msg) return detail.msg;
    try { return JSON.stringify(detail); } catch { return fallback; }
  }
  return fallback;
}
